import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow, parseISO } from 'date-fns';

const MessageMeta = ({ fromSelf, createdAt, readBy = [], currentUserId }) => {
    // 1. Ensure date is valid to prevent "Invalid Date" text in UI
    const messageDate = useMemo(() => {
        try {
            // ✅ SAFEGUARD: parseISO is much more reliable across all browsers
            const d = parseISO(createdAt);
            return isNaN(d.getTime()) ? new Date() : d;
        } catch (error) {
            // Fallback to exactly now if the timestamp is completely broken
            return new Date();
        }
    }, [createdAt]);

    const isRead = useMemo(() => {
        // Only the sender cares about the "Read" check
        if (!fromSelf || !Array.isArray(readBy)) return false;

        /**
         * ✅ ROBUST CHECK:
         * We check if ANY ID in the readBy array is different from the sender's ID.
         * This handles:
         * 1. readBy: ["id1", "id2"] (String array)
         * 2. readBy: [{user: {_id: "id1"}}] (Populated array)
         * 3. readBy: [{user: "id1"}] (Semi-populated)
         */
        return readBy.some(reader => {
            const readerId = reader?.user?._id || reader?.user?.id || reader?.user || reader;

            // If we found a valid ID that isn't the current user, it's been read.
            return readerId && String(readerId) !== String(currentUserId);
        });
    }, [readBy, fromSelf, currentUserId]);

    return (
        <div className="message-meta">
            <span className="timestamp">
                {formatDistanceToNow(messageDate, { addSuffix: true })}
            </span>
            {fromSelf && (
                <span className={`read-status ${isRead ? 'read' : 'sent'}`} title={isRead ? "Read by recipient" : "Delivered"}>
                    {/* Double tick for read, single tick for sent */}
                    {isRead ? '✔✔ Read' : '✔ Sent'}
                </span>
            )}
        </div>
    );
};

MessageMeta.propTypes = {
    fromSelf: PropTypes.bool.isRequired,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    readBy: PropTypes.array,
    currentUserId: PropTypes.string
};

export default MessageMeta;